﻿/** Global command & search palette (Ctrl+K). */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { KEYBOARD_EVENTS } from "@/lib/keyboard/keyboard-events";
import {
  Search,
  ShoppingCart,
  Package,
  Hammer,
  Factory,
  Users,
  FileSpreadsheet,
  Coins,
  Receipt,
  X,
  ArrowRight,
  Sparkles,
  User,
  Clock,
  Barcode,
  Settings,
} from "lucide-react";
import { usePeople } from "@/lib/people-store";
import { useStock } from "@/lib/stock-store";
import { useBilling } from "@/lib/billing-store";
import { useWorkers } from "@/lib/workers-store";
import { useOrders } from "@/lib/orders-store";
import {
  buildNavigationLauncherCatalog,
  filterLauncherCatalog,
} from "@/lib/navigation-launcher-catalog";

interface CommandItem {
  id: string;
  title: string;
  category: "Retail" | "Stock" | "Manufacturing" | "Accounts" | "Payroll" | "Reports" | "Settings" | "Customer" | "Invoice" | "Inventory" | "Karigar" | "Recent" | "Navigation";
  to: string;
  icon: React.ReactNode;
  subtitle?: string;
  shortcut?: string;
  keywords: string[];
}


const RECENTS_KEY = "ornexa_recent_searches_v2";

export const QuickCommandPalette: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  // Live entity stores
  const people = usePeople((s) => s.people);
  const stockItems = useStock((s) => s.items);
  const invoices = useBilling((s) => s.invoices);
  const orders = useOrders((s) => s.orders);

  // Recents state
  const [recents, setRecents] = useState<Array<{ id: string; title: string; to: string; category: string }>>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const saveRecent = (item: { id: string; title: string; to: string; category: string }) => {
    const updated = [item, ...recents.filter((r) => r.to !== item.to)].slice(0, 5);
    setRecents(updated);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
      } catch {
        /* Ignore */
      }
    }
  };

  // Toggle via global shortcut bus (Ctrl+K is handled in useGlobalShortcuts).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    const handleToggle = () => {
      setIsOpen((prev) => !prev);
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener(KEYBOARD_EVENTS.COMMAND_PALETTE, handleToggle);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener(KEYBOARD_EVENTS.COMMAND_PALETTE, handleToggle);
    };
  }, [isOpen]);

  const navCatalog = useMemo(() => buildNavigationLauncherCatalog(), []);

  // Combine static commands and live entity search
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();

    const catalogCommands: CommandItem[] = filterLauncherCatalog(navCatalog, q)
      .slice(0, 12)
      .map((item) => ({
        id: item.id,
        title: item.label,
        category: "Navigation",
        subtitle: item.description,
        to: item.to,
        icon: <ArrowRight className="h-4 w-4 text-gold" />,
        keywords: item.keywords,
      }));

    if (!q) {
      // Return recents if any, plus catalog primary destinations and static commands
      const recentCommands: CommandItem[] = recents.map((r) => ({
        id: `recent_${r.id}`,
        title: r.title,
        category: "Recent",
        to: r.to,
        icon: <Clock className="h-4 w-4 text-muted-foreground" />,
        keywords: [],
      }));
      return [...recentCommands, ...catalogCommands.slice(0, 14)];
    }

    // Live Customers (Direct 360 profile opening)
    const matchedPeople: CommandItem[] = Object.values(people)
      .filter((p) => p.fullName?.toLowerCase().includes(q) || p.phone?.includes(q) || (p.villageCity && p.villageCity.toLowerCase().includes(q)) || (p.area && p.area.toLowerCase().includes(q)))
      .slice(0, 5)
      .map((p) => ({
        id: `person_${p.id}`,
        title: p.fullName,
        category: "Customer",
        subtitle: `${p.phone || "No phone"} Â· ${p.villageCity || p.area || p.partyCode || "Customer"} (${p.type})`,
        to: `/people/${p.id}`,
        icon: <User className="h-4 w-4 text-blue-500" />,
        keywords: [p.fullName, p.phone || "", p.villageCity || p.area || ""],
      }));

    // Live Orders & Job Cards (Direct Order opening)
    const matchedOrders: CommandItem[] = Object.values(orders)
      .filter((o) => {
        const custName = people.find((p) => p.id === o.customerId)?.fullName || "";
        const itemType = o.items?.[0]?.itemName || o.item?.itemName || o.productionType || o.items?.[0]?.category || "";
        return o.orderNo?.toLowerCase().includes(q) || custName.toLowerCase().includes(q) || itemType.toLowerCase().includes(q);
      })
      .slice(0, 5)
      .map((o) => {
        const custName = people.find((p) => p.id === o.customerId)?.fullName || "Customer";
        const itemType = o.items?.[0]?.itemName || o.item?.itemName || o.productionType || o.items?.[0]?.category || "Jewellery";
        return {
          id: `order_${o.id}`,
          title: `Order ${o.orderNo} â€” ${itemType}`,
          category: "Manufacturing",
          subtitle: `${custName} Â· Status: ${o.status || "In Progress"}`,
          to: `/orders/${o.id}`,
          icon: <Factory className="h-4 w-4 text-purple-500" />,
          keywords: [o.orderNo || "", custName, itemType],
        };
      });

    // Live Ready Stock / Barcode (Direct Stock Item opening)
    const matchedStock: CommandItem[] = (Array.isArray(stockItems) ? stockItems : Object.values(stockItems || {}))
      .filter((s: any) => s.barcode?.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q))
      .slice(0, 5)
      .map((s: any) => ({
        id: `stock_${s.id || s.barcode}`,
        title: `${s.barcode || "Item"} - ${s.name || "Stock"}`,
        category: "Stock",
        subtitle: `${s.karat || "22"}K Â· Gross: ${s.grossWeight || 0}g Â· Net: ${s.netWeight || 0}g`,
        to: `/stock/${s.id || s.barcode}`,
        icon: <Barcode className="h-4 w-4 text-gold" />,
        keywords: [s.barcode || "", s.name || ""],
      }));

    // Live Invoices (Direct Invoice opening)
    const matchedInvoices: CommandItem[] = Object.values(invoices)
      .filter((inv) => inv.invoiceNo?.toLowerCase().includes(q) || inv.customerName?.toLowerCase().includes(q))
      .slice(0, 5)
      .map((inv) => ({
        id: `invoice_${inv.id}`,
        title: `Bill ${inv.invoiceNo}`,
        category: "Invoice",
        subtitle: `${inv.customerName || "Walk-in"} Â· â‚¹${((inv.grandTotalPaise || 0) / 100).toLocaleString("en-IN")}`,
        to: `/billing/${inv.id}`,
        icon: <Receipt className="h-4 w-4 text-emerald-500" />,
        keywords: [inv.invoiceNo, inv.customerName || ""],
      }));

    // Live Karigars (Artisans in people store - Direct Gold Book opening)
    const matchedWorkers: CommandItem[] = Object.values(people)
      .filter(
        (p) =>
          (p.type === "karigar" || p.type === "worker" || p.type === "outside_karigar" || p.roles?.includes("karigar")) &&
          (p.fullName?.toLowerCase().includes(q) || p.phone?.includes(q)),
      )
      .slice(0, 4)
      .map((p) => ({
        id: `worker_${p.id}`,
        title: p.fullName,
        category: "Karigar",
        subtitle: `Artisan / ${p.type} Â· ${p.phone || ""}`,
        to: `/workshop/gold-book?workerId=${p.id}`,
        icon: <Hammer className="h-4 w-4 text-amber-500" />,
        keywords: [p.fullName, p.phone || ""],
      }));

    return [
      ...catalogCommands,
      ...matchedPeople,
      ...matchedOrders,
      ...matchedStock,
      ...matchedInvoices,
      ...matchedWorkers,
    ];
  }, [query, people, stockItems, invoices, orders, recents, navCatalog]);

  const handleSelect = (item: CommandItem) => {
    saveRecent({ id: item.id, title: item.title, to: item.to, category: item.category });
    setIsOpen(false);
    setQuery("");
    navigate({ to: item.to as any });
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, filteredItems.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % Math.max(1, filteredItems.length));
    } else if (e.key === "Enter" && filteredItems[selectedIndex]) {
      e.preventDefault();
      handleSelect(filteredItems[selectedIndex]);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="quick-command-palette-modal"
      id="quick-command-palette-modal"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-16 md:pt-24 px-4 animate-in fade-in duration-150"
    >
      <div
        className="w-full max-w-2xl bg-card border border-border shadow-2xl rounded-2xl overflow-hidden flex flex-col"
        onKeyDown={handleListKeyDown}
      >
        {/* Omnisearch Input Bar */}
        <div className="flex items-center px-4 border-b border-border/80 bg-background/50">
          <Search className="h-5 w-5 text-muted-foreground mr-3 shrink-0" />
          <input
            autoFocus
            type="text"
            data-testid="quick-command-palette-input"
            id="quick-command-palette-input"
            placeholder="Search anything: 'New sale', customer name, invoice #, barcode tag, karigar..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            className="w-full h-14 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
          />
          <button
            type="button"
            data-testid="quick-command-palette-close"
            id="quick-command-palette-close"
            onClick={() => setIsOpen(false)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Results List */}
        <div className="max-h-96 overflow-y-auto p-2 space-y-1">
          {filteredItems.map((cmd, idx) => {
            const isSelected = idx === selectedIndex;
            return (
              <div
                key={cmd.id}
                onClick={() => handleSelect(cmd)}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${
                  isSelected ? "bg-primary text-primary-foreground font-medium" : "hover:bg-muted/60 text-foreground"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`p-2 rounded-lg shrink-0 ${
                      isSelected ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted"
                    }`}
                  >
                    {cmd.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{cmd.title}</div>
                    <div
                      className={`text-[11px] truncate ${
                        isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                      }`}
                    >
                      {cmd.subtitle || `${cmd.category} Â· Jump directly to workflow`}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {cmd.shortcut && (
                    <kbd
                      className={`hidden sm:inline-flex px-2 py-0.5 rounded text-[10px] font-mono ${
                        isSelected ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground border"
                      }`}
                    >
                      {cmd.shortcut}
                    </kbd>
                  )}
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider font-semibold ${
                      isSelected ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {cmd.category}
                  </span>
                  <ArrowRight
                    className={`h-4 w-4 ${isSelected ? "text-primary-foreground" : "text-muted-foreground"}`}
                  />
                </div>
              </div>
            );
          })}

          {filteredItems.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">No matching action, customer, or record found</p>
              <p className="text-xs">Try searching for a customer name, phone number, barcode tag, or task name.</p>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="p-3 bg-muted/40 border-t border-border/80 flex items-center justify-between text-[11px] text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Navigate:</span>
            <kbd className="px-1.5 py-0.5 rounded bg-background border font-mono">â†‘â†“</kbd>
            <kbd className="px-1.5 py-0.5 rounded bg-background border font-mono">Enter</kbd> to open
            <kbd className="px-1.5 py-0.5 rounded bg-background border font-mono">Esc</kbd> to dismiss
          </div>
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-gold" />
            <span>AVS Global Search & Navigator</span>
          </div>
        </div>
      </div>
    </div>
  );
};

